/**
 * Écran d'acceptation du DPA par l'organisateur (plan feat-acceptation-cgv-dpa, lot 5 ; décision
 * D1 = C de Valérie du 07/10/2026).
 *
 * Affiché à la connexion d'un ORG dont le compte n'a AUCUNE acceptation du DPA : clients antérieurs,
 * comptes créés hors formulaire (admin, KLive), rattachement manqué. Il bloque l'application jusqu'à
 * l'acceptation ; une seule acceptation couvre tout le compte, les autres profils ne le voient pas.
 * Le texte de la case est celui que le serveur enregistre (`legalCheckboxText`).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { legalDocumentsApi } from "../lib/api";
import { legalCheckboxParts, legalTextLanguage } from "../lib/legal-acceptance-texts";

export interface PendingDpa {
  dpa: { id: string; kind: "dpa"; version: string; effectiveOn: string; pdfSha256: string };
  companyName: string;
}

export function LegalAcceptanceGate({ pending, onAccepted, onReload, onLogout }: {
  pending: PendingDpa;
  onAccepted: () => void;
  onReload: () => void;
  onLogout: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lang = legalTextLanguage(i18n.language);
  // Le nom du document, dans la phrase, ouvre le PDF (nouvel onglet) : lisible avant de cocher.
  const parts = legalCheckboxParts("dpa", lang, { company: pending.companyName });

  const accept = async () => {
    setBusy(true); setError(null);
    try {
      await legalDocumentsApi.acceptDpa(pending.dpa.id, lang);
      onAccepted();
    } catch (e) {
      const err = e as Error & { code?: string };
      setError(err.message || t("common.error"));
      if (err.code === "legal_documents_changed") { setChecked(false); onReload(); }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>
            <ShieldCheck className="h-5 w-5" style={{ color: "#5A4BBE" }} />{t("legalGate.title")}
          </CardTitle>
          <CardDescription>{t("legalGate.intro", { company: pending.companyName })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {lang === "en" && <p className="text-xs text-muted-foreground">{t("signup.legal.frenchOnly")}</p>}
          <label className="flex items-start gap-2 text-sm" style={{ color: "#384872" }}>
            <input id="accept-dpa-gate" type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {parts.before}
              <a href={legalDocumentsApi.publicPdfUrl(pending.dpa.id)} target="_blank" rel="noopener noreferrer" className="font-medium underline" style={{ color: "#5A4BBE" }}>{parts.link}</a>
              {parts.after}
            </span>
          </label>
          {error && <p role="alert" className="text-sm" style={{ color: "#C0423F" }}>{error}</p>}
          <Button className="w-full" onClick={() => void accept()} disabled={!checked || busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("legalGate.accept")}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            {t("legalGate.notAuthorised")}{" "}
            <button type="button" className="underline" onClick={onLogout}>{t("legalGate.logout")}</button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
